import { type Filament, type Nozzle, type VariantProfile, formatBRL } from '@deizi/shared';
import { useState } from 'react';
import { AffixInput, Field, Seg } from '../../components/Form';
import { Icon } from '../../components/Icon';
import { useFilaments, useKits, usePrinters, useSupplies } from '../../lib/catalog-api';
import {
  durationInput,
  formatGrams,
  formatHm,
  parseDecimal,
  parseDuration,
} from '../../lib/numbers';
import { FilamentPicker, SupplyPicker, Swatch, filamentSub } from '../quote/Pickers';
import { Combobox } from '../../components/Combobox';
import { kitOption } from '../../components/pickers';

export interface SlotForm {
  key: string;
  filamentId: string | null;
  grams: string;
  nozzle: Nozzle | null;
}
export interface PlateForm {
  key: string;
  time: string;
  units: string;
  slots: SlotForm[];
}
export interface ProfileForm {
  printerIds: string[];
  plates: PlateForm[];
  batchPlates: string;
  finishingMin: string;
  packingMin: string;
  kitId: string;
  supplies: { supplyId: string; qty: string }[];
}

let seq = 0;
const key = () => `p${(seq++).toString(36)}`;
const dec = (n: number) => String(n).replace('.', ',');

export function emptySlot(): SlotForm {
  return { key: key(), filamentId: null, grams: '', nozzle: null };
}
export function emptyPlate(): PlateForm {
  return { key: key(), time: '', units: '1', slots: [emptySlot()] };
}

export function formOf(p: VariantProfile): ProfileForm {
  return {
    printerIds: p.printerIds,
    plates: (p.plates.length ? p.plates : []).map((pl) => ({
      key: key(),
      time: durationInput(pl.printSeconds),
      units: String(pl.unitsPerPlate),
      slots: pl.slots.map((s) => ({
        key: key(),
        filamentId: s.filamentId,
        grams: dec(s.grams),
        nozzle: s.nozzle,
      })),
    })),
    batchPlates: String(p.batchPlates),
    finishingMin: dec(p.labor.finishingSecondsPerUnit / 60),
    packingMin: dec(p.labor.packingSecondsPerUnit / 60),
    kitId: p.packagingKitId ?? '',
    supplies: p.supplies.map((s) => ({ supplyId: s.supplyId, qty: dec(s.quantityPerUnit) })),
  };
}

const int = (t: string, min: number) => Math.max(min, Math.round(parseDecimal(t) ?? min));

/** Form → API profile (lenient: unparsable numbers become 0 and the cost explains what is missing). */
export function profileOf(f: ProfileForm): VariantProfile {
  return {
    printerIds: f.printerIds,
    plates: f.plates.map((p) => ({
      printSeconds: parseDuration(p.time) ?? 0,
      unitsPerPlate: int(p.units, 1),
      slots: p.slots.map((s) => ({
        filamentId: s.filamentId,
        grams: Math.round((parseDecimal(s.grams) ?? 0) * 10) / 10,
        nozzle: s.nozzle,
      })),
    })),
    batchPlates: int(f.batchPlates, 1),
    labor: {
      finishingSecondsPerUnit: Math.round((parseDecimal(f.finishingMin) ?? 0) * 60),
      packingSecondsPerUnit: Math.round((parseDecimal(f.packingMin) ?? 0) * 60),
    },
    packagingKitId: f.kitId || null,
    supplies: f.supplies
      .map((s) => ({ supplyId: s.supplyId, quantityPerUnit: parseDecimal(s.qty) ?? 0 }))
      .filter((s) => s.quantityPerUnit > 0),
  };
}

type Update = (fn: (f: ProfileForm) => ProfileForm) => void;

function SlotRow({
  plate,
  slot,
  i,
  update,
  filaments,
  canRemove,
}: {
  plate: PlateForm;
  slot: SlotForm;
  i: number;
  update: Update;
  filaments: Filament[];
  canRemove: boolean;
}) {
  const [picking, setPicking] = useState(false);
  const f = filaments.find((x) => x.id === slot.filamentId);
  const set = (patch: Partial<SlotForm>) =>
    update((form) => ({
      ...form,
      plates: form.plates.map((p) =>
        p.key === plate.key
          ? { ...p, slots: p.slots.map((s) => (s.key === slot.key ? { ...s, ...patch } : s)) }
          : p,
      ),
    }));
  return (
    <div className="col" style={{ gap: 6 }}>
      <span className="lbl">Slot {i + 1}</span>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `minmax(0,1fr) 92px${canRemove ? ' 36px' : ''}`,
          gap: 8,
          alignItems: 'center',
        }}
      >
        <button
          type="button"
          className="picker"
          aria-haspopup="dialog"
          aria-label={`Filamento do slot ${i + 1}`}
          onClick={() => setPicking(true)}
        >
          <Swatch color={f?.colorHex ?? null} />
          <span className="pk-main">
            <span className="trunc">{f ? f.name : 'Escolher filamento'}</span>
            <span className="pk-sub">{f ? filamentSub(f) : 'Preço por kg do catálogo'}</span>
          </span>
          <Icon name="chevronRight" size={18} />
        </button>
        <AffixInput
          value={slot.grams}
          unit="g"
          placeholder="0"
          ariaLabel={`Gramas do slot ${i + 1}`}
          onChange={(v) => set({ grams: v })}
        />
        {canRemove ? (
          <button
            type="button"
            className="btn icon sm g"
            aria-label={`Remover slot ${i + 1}`}
            onClick={() =>
              update((form) => ({
                ...form,
                plates: form.plates.map((p) =>
                  p.key === plate.key
                    ? { ...p, slots: p.slots.filter((s) => s.key !== slot.key) }
                    : p,
                ),
              }))
            }
          >
            <Icon name="close" size={16} />
          </button>
        ) : null}
      </div>
      <Seg
        label={`Bico do slot ${i + 1}`}
        value={slot.nozzle}
        options={[
          { value: 'main', label: 'Bico principal' },
          { value: 'aux', label: 'Bico auxiliar' },
        ]}
        onChange={(v) => set({ nozzle: v })}
      />
      {picking ? (
        <FilamentPicker
          title={`Filamento do slot ${i + 1}`}
          filaments={filaments}
          selectedId={slot.filamentId}
          material={null}
          onClose={() => setPicking(false)}
          onChoose={(fil) => {
            set({ filamentId: fil.id });
            setPicking(false);
          }}
        />
      ) : null}
    </div>
  );
}

/** Print profile + bill of materials + labor (variant drawer, board Produto-Visao). */
export function ProfileEditor({ form, update }: { form: ProfileForm; update: Update }) {
  const printers = usePrinters();
  const filaments = useFilaments();
  const kits = useKits();
  const supplies = useSupplies();
  const [addingSupply, setAddingSupply] = useState(false);
  const fil = filaments.data?.data ?? [];
  const supplyList = supplies.data?.data ?? [];

  return (
    <div className="col" style={{ gap: 18 }}>
      <section className="col" style={{ gap: 12 }}>
        <h3>Perfil de impressão</h3>
        <Field label="Impressoras compatíveis" help="A primeira marcada entra no custo.">
          <div className="row wrap" style={{ gap: 8 }}>
            {(printers.data?.data ?? []).map((p) => {
              const on = form.printerIds.includes(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  className={on ? 'chip on' : 'chip'}
                  aria-pressed={on}
                  onClick={() =>
                    update((f) => ({
                      ...f,
                      printerIds: on
                        ? f.printerIds.filter((x) => x !== p.id)
                        : [...f.printerIds, p.id],
                    }))
                  }
                >
                  {on ? <Icon name="check" size={14} /> : null}
                  {p.name}
                </button>
              );
            })}
          </div>
        </Field>
        {form.plates.map((plate, pi) => (
          <div
            key={plate.key}
            className="card sunk col"
            style={{ gap: 10, padding: 12, borderRadius: 12 }}
          >
            <div className="row between">
              <span style={{ fontWeight: 600 }}>Placa {pi + 1}</span>
              {form.plates.length > 1 ? (
                <button
                  type="button"
                  className="btn sm g"
                  onClick={() =>
                    update((f) => ({ ...f, plates: f.plates.filter((p) => p.key !== plate.key) }))
                  }
                >
                  Remover placa
                </button>
              ) : null}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="Tempo da placa (h:mm)">
                <AffixInput
                  value={plate.time}
                  inputMode="text"
                  placeholder="0:00"
                  ariaLabel={`Tempo da placa ${pi + 1}`}
                  onChange={(v) =>
                    update((f) => ({
                      ...f,
                      plates: f.plates.map((p) => (p.key === plate.key ? { ...p, time: v } : p)),
                    }))
                  }
                />
              </Field>
              <Field label="Un. por placa">
                <AffixInput
                  value={plate.units}
                  inputMode="numeric"
                  ariaLabel={`Unidades por placa ${pi + 1}`}
                  onChange={(v) =>
                    update((f) => ({
                      ...f,
                      plates: f.plates.map((p) => (p.key === plate.key ? { ...p, units: v } : p)),
                    }))
                  }
                />
              </Field>
            </div>
            {plate.slots.map((s, i) => (
              <SlotRow
                key={s.key}
                plate={plate}
                slot={s}
                i={i}
                update={update}
                filaments={fil}
                canRemove={plate.slots.length > 1}
              />
            ))}
            <button
              type="button"
              className="btn sm g"
              style={{ alignSelf: 'flex-start', padding: 0 }}
              onClick={() =>
                update((f) => ({
                  ...f,
                  plates: f.plates.map((p) =>
                    p.key === plate.key ? { ...p, slots: [...p.slots, emptySlot()] } : p,
                  ),
                }))
              }
            >
              <Icon name="plus" size={16} />
              Slot
            </button>
          </div>
        ))}
        <div className="row wrap" style={{ gap: 12, alignItems: 'flex-end' }}>
          <button
            type="button"
            className="btn sm"
            onClick={() => update((f) => ({ ...f, plates: [...f.plates, emptyPlate()] }))}
          >
            <Icon name="plus" size={16} />
            Placa
          </button>
          <div style={{ width: 160 }}>
            <Field label="Placas por lote">
              <AffixInput
                value={form.batchPlates}
                inputMode="numeric"
                ariaLabel="Placas por lote"
                onChange={(v) => update((f) => ({ ...f, batchPlates: v }))}
              />
            </Field>
          </div>
        </div>
      </section>

      <section className="col" style={{ gap: 12 }}>
        <h3>Lista de materiais</h3>
        <Field id="pf-kit" label="Kit de embalagem">
          <Combobox
            id="pf-kit"
            value={form.kitId}
            options={[
              ...(kits.data?.data ?? []).map(kitOption),
              { value: '', label: 'Sem embalagem', pinned: 'bottom' },
            ]}
            onChange={(v) => update((f) => ({ ...f, kitId: v }))}
          />
        </Field>
        <Field label="Insumos por unidade">
          <div className="col" style={{ gap: 8 }}>
            {form.supplies.length === 0 ? (
              <p className="sm muted">Só filamento e o kit de embalagem.</p>
            ) : null}
            {form.supplies.map((it, i) => {
              const s = supplyList.find((x) => x.id === it.supplyId);
              return (
                <div
                  key={it.supplyId}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0,1fr) 72px 36px',
                    gap: 8,
                    alignItems: 'center',
                  }}
                >
                  <span className="sm">
                    {s?.name ?? 'Insumo removido'}{' '}
                    <span className="xs muted">
                      {s ? `${formatBRL(s.unitCostCentavos)} cada` : ''}
                    </span>
                  </span>
                  <input
                    className="input"
                    style={{ height: 36, textAlign: 'right', padding: '0 8px' }}
                    aria-label={`Quantidade de ${s?.name ?? 'insumo'}`}
                    value={it.qty}
                    onChange={(e) =>
                      update((f) => ({
                        ...f,
                        supplies: f.supplies.map((x, j) =>
                          j === i ? { ...x, qty: e.target.value } : x,
                        ),
                      }))
                    }
                  />
                  <button
                    type="button"
                    className="btn icon sm g"
                    aria-label={`Remover ${s?.name ?? 'insumo'}`}
                    onClick={() =>
                      update((f) => ({ ...f, supplies: f.supplies.filter((_, j) => j !== i) }))
                    }
                  >
                    <Icon name="close" size={16} />
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              className="btn sm g"
              style={{ alignSelf: 'flex-start', padding: 0 }}
              onClick={() => setAddingSupply(true)}
            >
              <Icon name="plus" size={16} />
              Insumo
            </button>
          </div>
        </Field>
      </section>

      <section className="col" style={{ gap: 12 }}>
        <h3>Mão de obra</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Acabamento">
            <AffixInput
              value={form.finishingMin}
              unit="min/un."
              ariaLabel="Acabamento"
              onChange={(v) => update((f) => ({ ...f, finishingMin: v }))}
            />
          </Field>
          <Field label="Embalagem">
            <AffixInput
              value={form.packingMin}
              unit="min/un."
              ariaLabel="Embalagem"
              onChange={(v) => update((f) => ({ ...f, packingMin: v }))}
            />
          </Field>
        </div>
      </section>

      {addingSupply ? (
        <SupplyPicker
          supplies={supplyList}
          chosen={form.supplies}
          onClose={() => setAddingSupply(false)}
          onAdd={(s) => {
            update((f) =>
              f.supplies.some((x) => x.supplyId === s.id)
                ? f
                : { ...f, supplies: [...f.supplies, { supplyId: s.id, qty: '1' }] },
            );
            setAddingSupply(false);
          }}
        />
      ) : null}
    </div>
  );
}

/** "Por unidade: 86 g · 3h10 · lote: 1 placa · 2 un. · 6h20". */
export function batchLine(cost: {
  perUnit: { grams: number; printSeconds: number };
  batch: { plates: number; units: number; printSeconds: number };
}): string {
  return `Por unidade: ${formatGrams(cost.perUnit.grams)} · ${formatHm(cost.perUnit.printSeconds)} · lote completo: ${cost.batch.plates} ${cost.batch.plates === 1 ? 'placa' : 'placas'} · ${cost.batch.units} un. · ${formatHm(cost.batch.printSeconds)}`;
}
